import {apiRequest} from "./apiRequest";
import type {
  Employee,
  QuotaChange,
  Quotas,
  QuotaChangeCreateRequest,
  QuotaUsageEvent,
  Subscription,
  TenantDetailsResponse,
  TenantUpdateRequest,
} from "../types/tenant";
import type {SubscriptionCreateRequest} from "../types/tenant-create";

export async function getTenantById(id: string): Promise<TenantDetailsResponse> {
  return apiRequest<TenantDetailsResponse>({path: `admin/tenants/${id}`});
}

export async function updateTenant(id: string, body: TenantUpdateRequest): Promise<TenantDetailsResponse> {
  await apiRequest<unknown>({
    path: `admin/tenants/${id}`,
    method: "PUT",
    body,
  });
  return getTenantById(id);
}

export async function deleteTenant(id: string): Promise<void> {
  return apiRequest<void>({path: `admin/tenants/${id}`, method: "DELETE"});
}

export async function createSubscription(tenantId: string, body: SubscriptionCreateRequest): Promise<Subscription> {
  return apiRequest<Subscription>({
    path: `admin/tenants/${tenantId}/subscriptions`,
    method: "POST",
    body,
  });
}

export type SubscriptionUpdateRequest = SubscriptionCreateRequest;

export async function updateSubscription(
  tenantId: string,
  subscriptionId: string,
  body: SubscriptionUpdateRequest,
): Promise<Subscription> {
  return apiRequest<Subscription>({
    path: `admin/tenants/${tenantId}/subscriptions/${subscriptionId}`,
    method: "PUT",
    body,
  });
}

export async function deleteSubscription(tenantId: string, subscriptionId: string): Promise<void> {
  return apiRequest<void>({
    path: `admin/tenants/${tenantId}/subscriptions/${subscriptionId}`,
    method: "DELETE",
  });
}

/** Current used / maximum per quota, read from the quota totals that quota changes adjust. */
export async function getQuotaTotals(tenantId: string): Promise<Quotas> {
  return apiRequest<Quotas>({path: `admin/quotas/${tenantId}`});
}

export async function getQuotaChanges(tenantId: string): Promise<QuotaChange[]> {
  return apiRequest<QuotaChange[]>({path: `admin/quotas/${tenantId}/changes`});
}

/**
 * Reports SMS usage only. E-mail usage events are recorded but excluded from this endpoint; the
 * storage quota has no usage events at all.
 */
export async function getQuotaUsage(tenantId: string): Promise<QuotaUsageEvent[]> {
  return apiRequest<QuotaUsageEvent[]>({path: `admin/quotas/${tenantId}/usage`});
}

export async function addQuotaChange(tenantId: string, body: QuotaChangeCreateRequest): Promise<unknown> {
  return apiRequest<unknown>({
    path: `admin/quotas/${tenantId}/changes`,
    method: "POST",
    body,
  });
}

export async function getEmployees(): Promise<Employee[]> {
  return apiRequest<Employee[]>({path: "admin/employees"});
}

export async function migrateAvailabilities(tenantId: string): Promise<void> {
  return apiRequest<void>({
    path: `admin/tenants/${tenantId}/migrate-availabilities`,
    method: "POST",
  });
}

export type TimeslotsCalculationStatus = {
  status?: string;
  lastRun?: string;
  [key: string]: unknown;
};

export async function triggerTimeslotsCalculation(tenantId: string): Promise<void> {
  return apiRequest<void>({
    path: `admin/tenants/${tenantId}/timeslots-calculations`,
    method: "POST",
  });
}

export async function getTimeslotsLatest(tenantId: string): Promise<TimeslotsCalculationStatus> {
  return apiRequest<TimeslotsCalculationStatus>({
    path: `admin/tenants/${tenantId}/timeslots-calculations/latest`,
  });
}
